import { ApiError } from '@/api/client';
import * as friendshipApi from '@/api/endpoints/friendship';
import { FRIENDS_QUERY_KEYS } from '@/api/queries/friendship';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook for friend-related mutations (send/accept/reject/remove)
 * Provides mutation functions with automatic cache invalidation
 */
export function useFriendActions() {
  const queryClient = useQueryClient();

  /**
   * Send a friend request
   */
  const sendRequest = useMutation({
    mutationFn: async (receiverId: string) => {
      return friendshipApi.sendFriendRequest(receiverId);
    },
    onMutate: async (receiverId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: FRIENDS_QUERY_KEYS.status(receiverId),
      });

      // Snapshot the previous value for rollback
      const previousStatus = queryClient.getQueryData(
        FRIENDS_QUERY_KEYS.status(receiverId)
      );

      // Optimistically update the status to "pending_sent"
      queryClient.setQueryData(
        FRIENDS_QUERY_KEYS.status(receiverId),
        {
          status: 'pending_sent' as const,
          requestId: undefined, // Will be set when the real response comes back
        }
      );

      // Return context with previous value for rollback
      return { previousStatus };
    },
    onSuccess: (data, receiverId) => {
      // Update with the real response data
      queryClient.setQueryData(
        FRIENDS_QUERY_KEYS.status(receiverId),
        {
          status: 'pending_sent' as const,
          requestId: data.id,
        }
      );

      // Invalidate requests queries to refresh the list
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.requests(),
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.sentRequests(),
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.suggested(),
      });
    },
    onError: (error, receiverId, context) => {
      const isConflict = error instanceof ApiError && error.status === 409;
      if (isConflict) {
        // A request already exists (e.g. the other user sent one first) - refetch to get correct state
        queryClient.invalidateQueries({
          queryKey: FRIENDS_QUERY_KEYS.status(receiverId),
        });
        return;
      }
      // Rollback to previous value on other errors
      if (context?.previousStatus) {
        queryClient.setQueryData(
          FRIENDS_QUERY_KEYS.status(receiverId),
          context.previousStatus
        );
      }
      console.error('[useFriendActions] Failed to send friend request:', error);
    },
  });

  /**
   * Accept a friend request
   */
  const acceptRequest = useMutation({
    mutationFn: async (requestId: string) => {
      return friendshipApi.acceptFriendRequest(requestId);
    },
    onSuccess: () => {
      // Invalidate friends list to show new friend
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.list(),
      });
      // Invalidate per-user friend lists (used by profile pages)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'user'] as const,
      });
      // Invalidate requests to remove accepted request
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.requests(),
      });
      // Invalidate all status queries (using prefix match)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'status'] as const,
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.suggested(),
      });
    },
    onError: (error) => {
      console.error('[useFriendActions] Failed to accept friend request:', error);
    },
  });

  /**
   * Reject a friend request
   */
  const rejectRequest = useMutation({
    mutationFn: async (requestId: string) => {
      return friendshipApi.rejectFriendRequest(requestId);
    },
    onSuccess: () => {
      // Invalidate requests to remove rejected request
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.requests(),
      });
      // Invalidate all status queries (using prefix match)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'status'] as const,
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.suggested(),
      });
    },
    onError: (error) => {
      console.error('[useFriendActions] Failed to reject friend request:', error);
    },
  });

  /**
   * Cancel a sent friend request
   */
  const cancelRequest = useMutation({
    mutationFn: async (requestId: string) => {
      return friendshipApi.cancelFriendRequest(requestId);
    },
    onSuccess: () => {
      // Invalidate sent requests to remove cancelled request
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.sentRequests(),
      });
      // Invalidate all status queries (using prefix match)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'status'] as const,
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.suggested(),
      });
    },
    onError: (error) => {
      console.error('[useFriendActions] Failed to cancel friend request:', error);
    },
  });

  /**
   * Remove a friend
   */
  const removeFriend = useMutation({
    mutationFn: async (userId: string) => {
      return friendshipApi.removeFriend(userId);
    },
    onSuccess: () => {
      // Invalidate friends list to remove friend
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.list(),
      });
      // Invalidate per-user friend lists (used by profile pages)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'user'] as const,
      });
      // Invalidate all status queries (using prefix match)
      queryClient.invalidateQueries({
        queryKey: ['friends', 'status'] as const,
      });
      queryClient.invalidateQueries({
        queryKey: FRIENDS_QUERY_KEYS.suggested(),
      });
    },
    onError: (error) => {
      console.error('[useFriendActions] Failed to remove friend:', error);
    },
  });

  return {
    sendRequest,
    acceptRequest,
    rejectRequest,
    cancelRequest,
    removeFriend,
  };
}
